import { EngagementOptionEnum } from '@forgd/shared/enums'
import { z } from 'zod'
import {
  BaseQuoteSchema,
  LoanCallOptionQuoteResponse,
  QuoteLoanCallOptionSchema,
  QuoteRetainerWorkingCapitalSchema,
  RetainerWorkingCapitalQuoteResponse,
} from '../market-maker-rfq'

const BaseQuoteTemplateSchema = BaseQuoteSchema.pick({
  id: true,
  engagementType: true,
  loanTokenSupplyPercentage: true,
  loanTokenQuantity: true,
  loanTokenSizeUsd: true,
  kpiOrderBookDominanceThreshold: true,
  uptimeObligationPercentage: true,
  kpiLoanValueDepth200BpsUsd: true,
  projectedLoanUtilizationPercentage: true,
  kpis: true,
  marketMakerComment: true,
  liquidityDistributionCex: true,
  liquidityDistributionDex: true,
  primaryCexVenue: true,
  primaryDexVenue: true,
}).extend({
  name: z.string(),
})

const ViewLoanCallOptionQuoteTemplateSchema = BaseQuoteTemplateSchema.merge(
  LoanCallOptionQuoteResponse.pick({
    loanOptionsStyle: true,
    loanTenorMonths: true,
    loanInterestRatePercentage: true,
    tranchesCount: true,
    tranches: true,
  }),
)
const ViewRetainerWorkingCapitalQuoteTemplateSchema = BaseQuoteTemplateSchema.merge(
  RetainerWorkingCapitalQuoteResponse.pick({
    loanStablecoinSizeUsd: true,
    loanCustody: true,
    onboardingFeeUsd: true,
    unlimitedExchangeCoverage: true,
    monthlyFeeUsd: true,
    minContractTermMonths: true,
    profitSharePercentage: true,
    unlimitedExchangeCoverageMonthlyFeeUsd: true,
    singleExchangeCoverageMonthlyFeeUsd: true,
  }),
)

const BaseEditQuoteTemplateSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(2, { message: 'Name must be at least 2 characters long' }),
})

export const GetQuoteTemplatesResponseSchema = z.object({
  [EngagementOptionEnum.LoanCallOption]: z.array(ViewLoanCallOptionQuoteTemplateSchema),
  [EngagementOptionEnum.RetainerWorkingCapital]: z.array(ViewRetainerWorkingCapitalQuoteTemplateSchema),
})

const CreateLoanCallOptionQuoteTemplateSchema = QuoteLoanCallOptionSchema.merge(
  BaseEditQuoteTemplateSchema.pick({ name: true }),
)
const CreateRetainerWorkingCapitalQuoteTemplateSchema = QuoteRetainerWorkingCapitalSchema.merge(
  BaseEditQuoteTemplateSchema.pick({ name: true }),
)
export const CreateQuoteTemplateRequestBodySchema = z.discriminatedUnion('engagementType', [
  CreateLoanCallOptionQuoteTemplateSchema,
  CreateRetainerWorkingCapitalQuoteTemplateSchema,
])

const UpdateLoanCallOptionQuoteTemplateSchema = QuoteLoanCallOptionSchema.merge(BaseEditQuoteTemplateSchema)
const UpdateRetainerWorkingCapitalQuoteTemplateSchema =
  QuoteRetainerWorkingCapitalSchema.merge(BaseEditQuoteTemplateSchema)
export const UpdateQuoteTemplateRequestBodySchema = z.discriminatedUnion('engagementType', [
  UpdateLoanCallOptionQuoteTemplateSchema,
  UpdateRetainerWorkingCapitalQuoteTemplateSchema,
])

// Exposed types
export type GetQuoteTemplatesResponseType = z.infer<typeof GetQuoteTemplatesResponseSchema>

export type CreateLoanCallOptionQuoteTemplateType = z.infer<typeof CreateLoanCallOptionQuoteTemplateSchema>
export type CreateRetainerWorkingCapitalQuoteTemplateType = z.infer<
  typeof CreateRetainerWorkingCapitalQuoteTemplateSchema
>

export type CreateQuoteTemplateRequestBodyType = z.infer<typeof CreateQuoteTemplateRequestBodySchema>
export type UpdateQuoteTemplateRequestBodyType = z.infer<typeof UpdateQuoteTemplateRequestBodySchema>

export type UpdateLoanCallOptionQuoteTemplateType = z.infer<typeof UpdateLoanCallOptionQuoteTemplateSchema>
export type UpdateRetainerWorkingCapitalQuoteTemplateType = z.infer<
  typeof UpdateRetainerWorkingCapitalQuoteTemplateSchema
>
